/**
 * ActivityPub adapter factory.
 *
 * Mastodon and Pixelfed both implement the ActivityPub protocol with near-identical
 * Mastodon-compatible APIs. This factory creates a PlatformAdapter for either platform
 * from a PlatformConfig, eliminating the duplicate code that previously existed between
 * mastodonAdapter.ts and pixelfedAdapter.ts.
 *
 * Usage:
 *   export const mastodonAdapter = createActivityPubAdapter(mastodonConfig)
 *   export const pixelfedAdapter = createActivityPubAdapter(pixelfedConfig)
 */

import type { ActivityPubSession } from '@/lib/mastodon'
import { isValidMastodonTagName } from '@/lib/mastodonTag'
import type { PostsPage, Post } from '@/types/posts'
import type { NotificationsPage } from '@/types/notifications'
import type {
    PlatformAdapter,
    PlatformCapabilities,
    AdapterSession,
    TimelineOptions,
    SearchResult,
    FollowStatus,
    BlockStatus,
    BlockedUsersPage,
} from './types'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'

// ---------------------------------------------------------------------------
// Platform config shape
// ---------------------------------------------------------------------------

/**
 * Service functions injected into the adapter. Each platform provides its own
 * implementations; the factory wires them into the unified PlatformAdapter interface.
 */
export interface ActivityPubServices {
    // Timeline
    fetchTimeline(
        session: ActivityPubSession,
        options: { cursor?: string; limit?: number; timeline?: 'local' | 'federated' }
    ): Promise<PostsPage>
    fetchListTimeline?(
        session: ActivityPubSession,
        listId: string,
        options: { maxId?: string }
    ): Promise<PostsPage>

    // Post mutation
    favouriteStatus(session: ActivityPubSession, id: string): Promise<void>
    unfavouriteStatus(session: ActivityPubSession, id: string): Promise<void>
    reblogStatus(session: ActivityPubSession, id: string): Promise<void>
    unreblogStatus(session: ActivityPubSession, id: string): Promise<void>
    deleteStatus(session: ActivityPubSession, id: string): Promise<void>

    // Favourites / bookmarks
    fetchFavourites(
        session: ActivityPubSession,
        options: { limit?: number; cursor?: string }
    ): Promise<PostsPage>
    bookmarkStatus?(session: ActivityPubSession, id: string): Promise<void>
    unbookmarkStatus?(session: ActivityPubSession, id: string): Promise<void>
    fetchBookmarks?(
        session: ActivityPubSession,
        options?: { cursor?: string; limit?: number }
    ): Promise<PostsPage>

    // Pinned posts. Only own statuses are pinnable, and fetchAPPosts always
    // requests the profile through the session's own instance (instanceOverride),
    // so today the id is always already local. pinPost/unpinPost still route it
    // through resolveStatusId (same as favourite/reblog/bookmark below) as
    // defense-in-depth — cheap (no network round-trip) when the id is already
    // local, and it stops a foreign id from being posted to the wrong account's
    // instance if that assumption ever stops holding.
    pinStatus?(session: ActivityPubSession, id: string): Promise<void>
    unpinStatus?(session: ActivityPubSession, id: string): Promise<void>

    // Search
    searchUsers(
        query: string,
        session: ActivityPubSession
    ): Promise<Array<{ id: string; display?: string; avatar?: string }>>
    searchPosts(query: string, session: ActivityPubSession, offset?: number): Promise<PostsPage>

    // Notifications
    fetchNotifications(
        session: ActivityPubSession,
        options: { limit?: number; cursor?: string } | undefined,
        platform: 'mastodon' | 'pixelfed'
    ): Promise<NotificationsPage>
    fetchUnreadCount(session: ActivityPubSession): Promise<{ count: number }>

    // Profile
    fetchProfilePosts(
        identifier: string,
        options: { cursor?: string; session: ActivityPubSession }
    ): Promise<PostsPage>

    // Hashtags
    fetchHashtagPosts(tag: string, session: ActivityPubSession, cursor?: string): Promise<PostsPage>

    // Social graph
    lookupAccount(session: ActivityPubSession, identifier: string): Promise<{ id: string }>
    getRelationship(
        session: ActivityPubSession,
        id: string
    ): Promise<{ following: boolean; blocking: boolean }>
    followAccount(session: ActivityPubSession, id: string): Promise<void>
    unfollowAccount(session: ActivityPubSession, id: string): Promise<void>
    blockUser?(session: ActivityPubSession, id: string): Promise<void>
    unblockUser?(session: ActivityPubSession, id: string): Promise<void>
    fetchBlocks?(
        session: ActivityPubSession,
        options?: { cursor?: string; limit?: number }
    ): Promise<{
        accounts: Array<{ id: string; handle: string; displayName?: string; avatar?: string }>
        cursor?: string
    }>

    /**
     * Resolve a remote status ID to the local instance's ID.
     * Mastodon requires this for cross-instance engagement; Pixelfed does not.
     * When omitted, the post's mastodonStatusId is used directly.
     */
    resolveLocalStatusId?(
        session: ActivityPubSession,
        uri: string,
        statusId: string
    ): Promise<string>
}

/**
 * Platform-level configuration that drives per-platform behavior differences.
 */
export interface ActivityPubPlatformConfig {
    /** The platform identifier ('mastodon' or 'pixelfed') */
    platform: 'mastodon' | 'pixelfed'
    /** Platform-specific capability flags */
    capabilities: PlatformCapabilities
    /** URI prefix used for timeline feed URIs (e.g. 'mastodon' → 'mastodon:local') */
    timelinePrefix: string
    /** Whether this platform supports list timelines */
    supportsListTimeline: boolean
    /** Whether this platform supports followed-hashtag timelines as Home tabs (`<prefix>-hashtag:` URIs) */
    supportsHashtagTimeline: boolean
    /** Service implementations for this platform */
    services: ActivityPubServices
}

// ---------------------------------------------------------------------------
// Account ID lookup cache (shared across adapter instances for the same process)
// ---------------------------------------------------------------------------

const accountIdCache = new Map<string, { id: string; expiresAt: number }>()
const ACCOUNT_ID_CACHE_TTL = 60 * 60 * 1000 // 1 hour

async function lookupAccountCached(
    services: ActivityPubServices,
    session: ActivityPubSession,
    identifier: string
): Promise<{ id: string }> {
    const key = `${session.instance_url}:${identifier}`
    const cached = accountIdCache.get(key)
    if (cached && Date.now() < cached.expiresAt) {
        return { id: cached.id }
    }
    const account = await services.lookupAccount(session, identifier)
    accountIdCache.set(key, { id: account.id, expiresAt: Date.now() + ACCOUNT_ID_CACHE_TTL })
    return account
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a PlatformAdapter for an ActivityPub-based platform (Mastodon, Pixelfed).
 * Pass the platform config — capabilities, service implementations, and behavioral flags.
 */
export function createActivityPubAdapter(config: ActivityPubPlatformConfig): PlatformAdapter {
    const {
        platform,
        capabilities,
        timelinePrefix,
        supportsListTimeline,
        supportsHashtagTimeline,
        services,
    } = config

    async function resolveStatusId(session: ActivityPubSession, post: Post): Promise<string> {
        if (post.mastodonStatusId) {
            if (services.resolveLocalStatusId) {
                return services.resolveLocalStatusId(session, post.uri, post.mastodonStatusId)
            }
            return post.mastodonStatusId
        }
        // Bridged AT Protocol post: construct Bridgy Fed ActivityPub URL and resolve via
        // the home instance's search API (resolveLocalStatusId → /api/v2/search?resolve=true)
        if (post.uri.startsWith('at://') && services.resolveLocalStatusId) {
            const bridgyUrl = `https://bsky.brid.gy/convert/ap/${post.uri}`
            return services.resolveLocalStatusId(session, bridgyUrl, bridgyUrl)
        }
        // Native AP post without mastodonStatusId: resolve via the post URI
        // (which is already an ActivityPub URL) through the home instance's search API
        if (post.uri && services.resolveLocalStatusId) {
            return services.resolveLocalStatusId(session, post.uri, post.uri)
        }
        throw new Error(`Cannot engage with post: missing mastodonStatusId`)
    }

    return {
        platform: platform as Platform,
        capabilities,

        // --- Timeline ---

        async fetchTimeline(session: AdapterSession, options?: TimelineOptions) {
            const s = session as ActivityPubSession

            if (supportsListTimeline && options?.feedUri?.startsWith(`${timelinePrefix}-list:`)) {
                if (!services.fetchListTimeline) {
                    throw new Error(`${platform} does not support list timelines`)
                }
                const listId = options.feedUri.slice(`${timelinePrefix}-list:`.length)
                if (!listId) throw new Error(`Invalid ${timelinePrefix}-list URI: missing list ID`)
                return services.fetchListTimeline(s, listId, { maxId: options.cursor })
            }

            if (
                supportsHashtagTimeline &&
                options?.feedUri?.startsWith(`${timelinePrefix}-hashtag:`)
            ) {
                const tag = options.feedUri.slice(`${timelinePrefix}-hashtag:`.length)
                if (!tag) throw new Error(`Invalid ${timelinePrefix}-hashtag URI: missing hashtag`)
                if (!isValidMastodonTagName(tag)) {
                    throw new Error(`Invalid ${timelinePrefix}-hashtag URI: invalid hashtag`)
                }
                const page = await services.fetchHashtagPosts(tag, s, options.cursor)
                // useTimeline paginates on `cursor` (hooks/useTimeline.ts), but
                // fetchMastodonHashtagPosts returns `mastodonCursor` (the field the
                // cross-protocol hashtag page reads). Remap so Home tab pagination works.
                return { posts: page.posts, cursor: page.mastodonCursor ?? page.cursor }
            }

            const timeline =
                options?.feedUri === `${timelinePrefix}:local`
                    ? 'local'
                    : options?.feedUri === `${timelinePrefix}:federated`
                      ? 'federated'
                      : undefined

            return services.fetchTimeline(s, {
                cursor: options?.cursor,
                limit: options?.limit,
                timeline,
            })
        },

        // --- Post engagement ---

        async likePost(session: AdapterSession, post) {
            const s = session as ActivityPubSession
            const localId = await resolveStatusId(s, post)
            await services.favouriteStatus(s, localId)
            return { uri: `${timelinePrefix}:favourited:${localId}`, liked: true }
        },

        async unlikePost(session: AdapterSession, post) {
            const s = session as ActivityPubSession
            const localId = await resolveStatusId(s, post)
            await services.unfavouriteStatus(s, localId)
        },

        async repostPost(session: AdapterSession, post) {
            const s = session as ActivityPubSession
            const localId = await resolveStatusId(s, post)
            await services.reblogStatus(s, localId)
            return { uri: `${timelinePrefix}:reblogged:${localId}`, reposted: true }
        },

        async unrepostPost(session: AdapterSession, post) {
            const s = session as ActivityPubSession
            const localId = await resolveStatusId(s, post)
            await services.unreblogStatus(s, localId)
        },

        async deletePost(session: AdapterSession, post) {
            const s = session as ActivityPubSession
            if (!post.mastodonStatusId)
                throw new Error('Cannot engage with post: missing mastodonStatusId')
            await services.deleteStatus(s, post.mastodonStatusId)
        },

        // --- Search ---

        async searchUsers(session: AdapterSession, query: string): Promise<SearchResult[]> {
            const s = session as ActivityPubSession
            const results = await services.searchUsers(query, s)
            return results.map(r => ({
                handle: r.id,
                displayName: r.display,
                avatarUrl: r.avatar,
                platform: platform as Platform,
            }))
        },

        async searchPosts(session: AdapterSession, query: string, cursor?: string) {
            const s = session as ActivityPubSession
            const offset = cursor ? parseInt(cursor, 10) : undefined
            return services.searchPosts(query, s, offset)
        },

        // --- Notifications ---

        async fetchNotifications(session: AdapterSession, options?) {
            const s = session as ActivityPubSession
            return services.fetchNotifications(s, options, platform)
        },

        async getUnreadCount(session: AdapterSession) {
            const s = session as ActivityPubSession
            const result = await services.fetchUnreadCount(s)
            return result.count
        },

        // --- Author posts ---

        async fetchAuthorPosts(session: AdapterSession, identifier: string, options?) {
            const s = session as ActivityPubSession
            return services.fetchProfilePosts(identifier, {
                cursor: options?.cursor,
                session: s,
            })
        },

        // --- Follow ---

        async getFollowStatus(session: AdapterSession, identifier: string): Promise<FollowStatus> {
            const s = session as ActivityPubSession
            const account = await lookupAccountCached(services, s, identifier)
            const relationship = await services.getRelationship(s, account.id)
            return { isFollowing: relationship.following, followRef: account.id }
        },

        async follow(session: AdapterSession, identifier: string): Promise<FollowStatus> {
            const s = session as ActivityPubSession
            const account = await lookupAccountCached(services, s, identifier)
            await services.followAccount(s, account.id)
            return { isFollowing: true, followRef: account.id }
        },

        async unfollow(
            session: AdapterSession,
            _identifier: string,
            followRef?: string
        ): Promise<void> {
            const s = session as ActivityPubSession
            if (!followRef) throw new Error('Cannot unfollow: missing account ID')
            await services.unfollowAccount(s, followRef)
        },

        // --- Block (optional — only provided when platform supports it) ---

        ...(services.blockUser && services.unblockUser && services.fetchBlocks
            ? {
                  async getBlockStatus(
                      session: AdapterSession,
                      identifier: string
                  ): Promise<BlockStatus> {
                      const s = session as ActivityPubSession
                      const account = await lookupAccountCached(services, s, identifier)
                      const relationship = await services.getRelationship(s, account.id)
                      return { isBlocked: relationship.blocking, blockRef: account.id }
                  },

                  async blockUser(
                      session: AdapterSession,
                      identifier: string
                  ): Promise<BlockStatus> {
                      const s = session as ActivityPubSession
                      const account = await lookupAccountCached(services, s, identifier)
                      await services.blockUser!(s, account.id)
                      return { isBlocked: true, blockRef: account.id }
                  },

                  async unblockUser(
                      session: AdapterSession,
                      _identifier: string,
                      blockRef?: string
                  ): Promise<void> {
                      const s = session as ActivityPubSession
                      if (!blockRef) throw new Error('Cannot unblock: missing account ID')
                      await services.unblockUser!(s, blockRef)
                  },

                  async getBlockedUsers(
                      session: AdapterSession,
                      options?: { cursor?: string; limit?: number }
                  ): Promise<BlockedUsersPage> {
                      const s = session as ActivityPubSession
                      const page = await services.fetchBlocks!(s, options)
                      return {
                          users: page.accounts.map(a => ({
                              id: a.id,
                              handle: a.handle,
                              displayName: a.displayName,
                              avatar: a.avatar,
                              blockRef: a.id,
                          })),
                          cursor: page.cursor,
                      }
                  },
              }
            : {}),

        // --- Favourites ---

        async fetchFavourites(session: AdapterSession, options?) {
            const s = session as ActivityPubSession
            return services.fetchFavourites(s, {
                limit: options?.limit ?? 25,
                cursor: options?.cursor,
            })
        },

        // --- Bookmarks (optional) ---

        ...(services.bookmarkStatus && services.unbookmarkStatus && services.fetchBookmarks
            ? {
                  async bookmarkPost(session: AdapterSession, post) {
                      const s = session as ActivityPubSession
                      const id = await resolveStatusId(s, post)
                      await services.bookmarkStatus!(s, id)
                      return {
                          uri: `${timelinePrefix}:bookmarked:${id}`,
                          bookmarked: true,
                      }
                  },

                  async unbookmarkPost(session: AdapterSession, post) {
                      const s = session as ActivityPubSession
                      const id = await resolveStatusId(s, post)
                      await services.unbookmarkStatus!(s, id)
                  },

                  async fetchBookmarks(session: AdapterSession, options?) {
                      const s = session as ActivityPubSession
                      return services.fetchBookmarks!(s, options)
                  },
              }
            : {}),

        // --- Pinned posts (optional) ---

        ...(services.pinStatus && services.unpinStatus
            ? {
                  async pinPost(session: AdapterSession, post: Post) {
                      const s = session as ActivityPubSession
                      if (!post.mastodonStatusId)
                          throw new Error('Cannot pin post: missing mastodonStatusId')
                      const id = await resolveStatusId(s, post)
                      await services.pinStatus!(s, id)
                  },

                  async unpinPost(session: AdapterSession, post: Post) {
                      const s = session as ActivityPubSession
                      if (!post.mastodonStatusId)
                          throw new Error('Cannot unpin post: missing mastodonStatusId')
                      const id = await resolveStatusId(s, post)
                      await services.unpinStatus!(s, id)
                  },
              }
            : {}),

        // --- Hashtags ---

        async fetchHashtagPosts(session: AdapterSession, tag: string, cursor?: string) {
            const s = session as ActivityPubSession
            return services.fetchHashtagPosts(tag, s, cursor)
        },
    }
}
